/** Upload flow (spec §8.10): hash → AssetRef in Loro → OPFS → queue. */
import type { AssetRef } from "@kss/schema";
import { putBlob } from "../offline/opfsBlobCache";
import { enqueueUpload } from "../offline/uploadQueue";
import { sha256File } from "./hash";

async function probe(file: File): Promise<Partial<AssetRef>> {
  const out: Partial<AssetRef> = {};
  try {
    if (file.type.startsWith("image/")) {
      const bmp = await createImageBitmap(file);
      out.width = bmp.width;
      out.height = bmp.height;
      bmp.close();
    } else if (file.type.startsWith("audio/") || file.type.startsWith("video/")) {
      const el = document.createElement(file.type.startsWith("audio/") ? "audio" : "video");
      const url = URL.createObjectURL(file);
      await new Promise<void>((res) => {
        el.onloadedmetadata = () => res();
        el.onerror = () => res();
        el.src = url;
      });
      if (Number.isFinite(el.duration)) out.durationMs = Math.round(el.duration * 1000);
      if (el instanceof HTMLVideoElement && el.videoWidth) {
        out.width = el.videoWidth;
        out.height = el.videoHeight;
      }
      URL.revokeObjectURL(url);
    }
  } catch {
    /* best effort */
  }
  return out;
}

export async function prepareAsset(file: File, onHashProgress?: (p: number) => void): Promise<AssetRef> {
  const [hash, dims] = await Promise.all([sha256File(file, onHashProgress), probe(file)]);
  await putBlob(hash, file);
  return { hash, mime: file.type || "application/octet-stream", size: file.size, name: file.name, ...dims };
}

export async function queueAssetUpload(ref: AssetRef, crdtDocId: string, treeId: string): Promise<void> {
  await enqueueUpload({ hash: ref.hash, name: ref.name, mime: ref.mime, size: ref.size, crdtDocId, treeId });
}
